/**
 * As queries do módulo Orçamento — **API User**.
 *
 * 🔒 **Todo caminho vive sob `/orcamento/`**, como o FinOps vive sob `/finops/`.
 * Não é estética: é o que deixa o API Gateway servir o módulo inteiro com UM
 * `{proxy+}` apontando para UMA Lambda, em vez de uma árvore de rotas por
 * agregado. A primeira versão destas queries usava `/contratos` e `/faturas` na
 * raiz, e teria exigido quatro entradas de rota para sempre.
 *
 * 🔒 **Toda consulta leva `idCliente`**, e ele é o primeiro argumento posicional
 * de `chaveDeQuery` (BR-19). Trocar de cliente troca a chave, e o TanStack Query
 * invalida em vez de mesclar.
 *
 * 🔒 **Nenhuma conversão de moeda acontece aqui nem nas telas.** Os agregados
 * chegam prontos porque só o servidor sabe quais parâmetros valiam em cada
 * período (BR-21). Se alguma tela deste módulo um dia multiplicar um valor por
 * uma cotação, a regra morreu ali.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { apiUser } from '@/api/clients/apiUser'
import { aditivoSchema, contratoSchema } from '@/api/contracts/contrato'
import { faturaSchema } from '@/api/contracts/fatura'
import { lancamentoManualSchema } from '@/api/contracts/lancamento'
import { dashboardOrcamentarioSchema } from '@/api/contracts/orcamentoDashboard'

import { chaveDeQuery } from './chaveDeQuery'

// ── 2i — Dashboard orçamentário ─────────────────────────────────────────────
/**
 * 🔴 `competencia` é o seletor de mês da `2i`, e entra na CHAVE.
 *
 * Sem ele na chave, trocar de mês devolveria o cache do mês anterior — o mesmo
 * erro que BR-19 previne para cliente, um nível abaixo. `undefined` significa
 * "o mês corrente que o servidor resolver", que é o estado inicial da tela.
 */
export function useDashboardOrcamentario(idCliente: string, competencia?: string) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'orcamento-dashboard', competencia),
    queryFn: async () =>
      dashboardOrcamentarioSchema.parse(
        await apiUser.get<unknown>('/orcamento/dashboard', {
          params: competencia ? { idCliente, competencia } : { idCliente },
        }),
      ),
  })
}

// ── 2j — Contratos & aditivos ───────────────────────────────────────────────
export function useContratos(idCliente: string) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'contratos'),
    queryFn: async () =>
      z
        .array(contratoSchema)
        .parse(await apiUser.get<unknown>('/orcamento/contratos', { params: { idCliente } })),
  })
}

/**
 * Os aditivos de um contrato, **já ordenados por `vigenteAPartirDe`** pelo
 * servidor.
 *
 * 🔒 A ordenação é do servidor de propósito (BR-21): resolver "quais parâmetros
 * valiam em junho" é caminhar essa lista em ordem cronológica, e cada tela que
 * inventasse a própria ordenação acertaria até o dia em que errasse em silêncio.
 */
export function useAditivos(idCliente: string, idContrato: string | undefined) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'aditivos', idContrato),
    enabled: Boolean(idContrato),
    queryFn: async () =>
      z
        .array(aditivoSchema)
        .parse(
          // 🔒 `idCliente` não é decoração: `gestao_orcamento.py` exige BR-19 em
          // TODA rota do módulo e devolve 400 CLIENTE_OBRIGATORIO antes mesmo de
          // rotear. Sem este parâmetro a lista de aditivos da tela 2j falhava
          // contra o gateway real, e só contra ele — o MSW não checava escopo.
          await apiUser.get<unknown>(`/orcamento/contratos/${idContrato}/aditivos`, {
            params: { idCliente },
          }),
        ),
  })
}

/**
 * Edita os parâmetros do contrato.
 *
 * 🔒 **Isto NÃO é a forma de repactuar.** Editar o contrato corrige o que foi
 * digitado errado; repactuar cria um ADITIVO, que vale da data dele para frente
 * e deixa o passado intacto (BR-21). Duas mutations distintas porque são dois
 * atos distintos — e porque juntá-las convidaria alguém a "repactuar" alterando
 * a PTAX no lugar, reescrevendo a história do cálculo.
 */
export function useSalvarContrato(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { idContrato: string; campos: Record<string, unknown> }) =>
      contratoSchema.parse(
        await apiUser.put<unknown>(`/orcamento/contratos/${params.idContrato}`, {
          idCliente,
          ...params.campos,
        }),
      ),
    onSuccess: () => void invalidarOrcamento(client, idCliente),
  })
}

export function useCriarContrato(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (campos: Record<string, unknown>) =>
      contratoSchema.parse(
        await apiUser.post<unknown>('/orcamento/contratos', { idCliente, ...campos }),
      ),
    onSuccess: () => void invalidarOrcamento(client, idCliente),
  })
}

/** 🔒 Cria uma linha nova. Nunca altera o contrato — é isso que BR-21 exige. */
export function useCriarAditivo(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { idContrato: string; campos: Record<string, unknown> }) =>
      aditivoSchema.parse(
        await apiUser.post<unknown>(`/orcamento/contratos/${params.idContrato}/aditivos`, {
          idCliente,
          ...params.campos,
        }),
      ),
    onSuccess: () => void invalidarOrcamento(client, idCliente),
  })
}

/**
 * Um aditivo muda os parâmetros do período corrente, e com eles o budget e o
 * custo calculado. Invalidar só a lista de aditivos deixaria o dashboard e a
 * reconciliação mostrando números de antes da repactuação.
 *
 * `lancamentos` entrou em 14/10/2026: `useCriarLancamento` também passa por aqui,
 * e sem ela a lista da `2k` só mostrava o lançamento novo depois de um F5.
 */
function invalidarOrcamento(client: ReturnType<typeof useQueryClient>, idCliente: string) {
  return Promise.all(
    ['contratos', 'aditivos', 'orcamento-dashboard', 'faturas', 'lancamentos'].map((recurso) =>
      client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, recurso) }),
    ),
  )
}

// ── 2k — Faturas & lançamentos ──────────────────────────────────────────────
export function useFaturas(idCliente: string) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'faturas'),
    queryFn: async () =>
      z
        .array(faturaSchema)
        .parse(await apiUser.get<unknown>('/orcamento/faturas', { params: { idCliente } })),
  })
}

export function useLancamentos(idCliente: string, competencia: string | undefined) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'lancamentos', competencia),
    enabled: Boolean(competencia),
    queryFn: async () =>
      z.array(lancamentoManualSchema).parse(
        await apiUser.get<unknown>('/orcamento/lancamentos', {
          params: { idCliente, competencia },
        }),
      ),
  })
}

/**
 * O botão `novo lançamento` da `2k`.
 *
 * 🔒 A tela NÃO manda `origem`: o servidor a fixa em `manual`. Um lançamento
 * criado aqui é manual por definição, e deixar o cliente escolher permitiria
 * forjar uma linha "integrada" que nenhuma integração produziu — quebrando
 * justamente o agrupamento que a `2i` mostra.
 */
export function useCriarLancamento(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: {
      competencia: string
      idContrato: string
      provedor: string
      tipo: 'desconto' | 'acrescimo'
      valor: number
      descricao: string
    }) =>
      lancamentoManualSchema.parse(
        await apiUser.post<unknown>('/orcamento/lancamentos', { idCliente, ...dados }),
      ),
    // O lançamento entra na soma da reconciliação E nos agrupamentos do
    // dashboard. Invalidar só a lista deixaria a diferença da fatura mentindo
    // na mesma tela em que a linha acabou de aparecer.
    onSuccess: () => void invalidarOrcamento(client, idCliente),
  })
}

/**
 * O botão `registrar fatura` da `2k`.
 *
 * 🔴 **Registrar não é aprovar.** O fluxo de aprovação da reconciliação está EM
 * ABERTO (§9 item 5): esta mutation grava o valor que o broker cobrou e nada
 * mais. Nenhuma mutation de aprovação existe neste arquivo, e a ausência é
 * deliberada — inventá-la seria inventar o processo da iOX.
 */
export function useRegistrarFatura(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { idFatura: string; valorFatura: number }) =>
      faturaSchema.parse(
        await apiUser.post<unknown>(`/orcamento/faturas/${params.idFatura}/registrar`, {
          idCliente,
          valorFatura: params.valorFatura,
        }),
      ),
    // O dashboard `2i` lê o mesmo consumo — registrar a fatura de um mês muda o
    // que ele mostra. Invalidar só a lista deixaria as duas abas discordando.
    onSuccess: () =>
      void Promise.all([
        client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'faturas') }),
        client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'orcamento-dashboard') }),
      ]),
  })
}
